import { z } from '@/utils/zod'

import { TenantEntitySchema } from './Entity'
import { ResourceServerIdSchema } from './ResourceServer'
import { createMultilingualTextSchema, StorageResourceSchema } from './common'
import { PresenterSchema } from './presenter'
import { AuthProviderSchema } from './authProvider'

export const ApplicationTypeSchema = z.enum(['native', 'machine', 'web', 'spa'])

export type ApplicationType = z.infer<typeof ApplicationTypeSchema>

export const GrantTypeSchema = z.enum(['implicit', 'authorization_code', 'refresh_token', 'client_credentials'])

export type GrantType = z.infer<typeof GrantTypeSchema>

export const EndpointAuthMethodSchema = z.enum([
    'client_secret_basic',
    'client_secret_jwt',
    'client_secret_post',
    'private_key_jwt',
    'none',
])

export type EndpointAuthMethod = z.infer<typeof EndpointAuthMethodSchema>

export const CustomLinkSchema = z.object({
    label: createMultilingualTextSchema(20),
    url: z.url(),
})

export type CustomLink = z.infer<typeof CustomLinkSchema>

export const AgreementSchema = z.object({
    id: z
        .string()
        .length(12)
        .regex(/^[A-Za-z0-9_-]+$/),
    text: createMultilingualTextSchema(200),
})

export type Agreement = z.infer<typeof AgreementSchema>

export const ApplicationCompatibilitySchema = z.object({
    claims: z.object({
        emailVerified: z.enum(['inherit', 'forceTrue']),
    }),
})

export type ApplicationCompatibility = z.infer<typeof ApplicationCompatibilitySchema>

export const ApplicationManagedBySchema = z.enum(['user', 'system'])

export type ApplicationManagedBy = z.infer<typeof ApplicationManagedBySchema>

export const ApplicationSchema = TenantEntitySchema.extend({
    name: z.string().min(1).max(120),
    description: z.string().min(1).max(1000),
    avatar: StorageResourceSchema.nullable(),
    presenter: PresenterSchema.nullable(),
    authProviders: z.uniqueArray(AuthProviderSchema).nullable(),

    managedBy: ApplicationManagedBySchema,
    firstParty: z.boolean(),
    handoff: z.object({
        issueTickets: z.boolean(),
        acceptTickets: z.boolean(),
    }),

    clientId: z.string().length(32),
    clientSecret: z.string().length(64),

    type: ApplicationTypeSchema,

    grantTypes: z.array(GrantTypeSchema),
    pkceRequired: z.boolean(),
    dpopRequired: z.boolean(),
    interactionTimeout: z.number().min(1),
    accessTokenTimeout: z.number().min(1),
    refreshTokenTimeout: z.number().min(1),
    clientCredentialsTimeout: z.number().min(1),
    idTokenTimeout: z.number().min(1),
    authCodeTimeout: z.number().min(1),
    grantTimeout: z.number().min(0),

    allowedResourceIds: z.uniqueArray(ResourceServerIdSchema),
    defaultResourceId: ResourceServerIdSchema.nullable(),
    compatibility: ApplicationCompatibilitySchema.nullable(),

    callbackUrls: z.array(z.url()),
    allowedCorsOrigins: z.array(z.url()),
    identitySyncUrl: z.union([z.url(), z.literal('')]),
    appUrl: z.union([z.url(), z.literal('')]),

    backchannelLogoutUrl: z.union([z.url(), z.literal('')]),
    postLogoutUrls: z.array(z.url()),
    alwaysShowLogoutPrompt: z.boolean(),

    endpointsAuthMethod: EndpointAuthMethodSchema,
    publicKeys: z.array(
        z.object({
            kid: z.string().max(64),
            kty: z.enum(['RSA']),
            n: z.string(),
            e: z.string(),
        })
    ),

    termsOfServiceUrl: z.union([z.url(), z.literal('')]),
    privacyPolicyUrl: z.union([z.url(), z.literal('')]),
    footerLinks: z.array(CustomLinkSchema),
    signupAgreements: z.array(AgreementSchema),
}).superRefine((application, ctx) => {
    if (application.defaultResourceId && !application.allowedResourceIds.includes(application.defaultResourceId)) {
        ctx.issues.push({
            code: 'custom',
            input: application.defaultResourceId,
            path: ['defaultResourceId'],
            message: 'The default resource must be included in allowedResourceIds',
        })
    }
})

export type Application = z.infer<typeof ApplicationSchema>
